import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { authApi, apiUrl, programsApi } from 'api';
import { useGlobal } from 'contexts/globalContext';
import { RegisterPayload } from 'types/payloads/RegisterPayload';
import { StoredKeys } from 'types/Auth';
import { Button } from 'components/_new/Button';
import TextField from 'components/_common/forms/TextField';
import { InputCheckbox } from 'components/_new/InputCheckbox';
import { colors } from 'theme';
import GoogleIcon from 'assets/icons/google-logo.svg';
import OfficeIcon from 'assets/icons/office-logo.svg';
import { translatedStaticResourceResolver } from 'utils/translatedStaticResourceResolver';
import { AxiosError } from 'axios';
import { generateAutocompleteId } from 'utils/generate-autocomplete-id';
import { Spinner } from 'components/_new/Spinner';
import { useProgramTranslationStatic } from 'hooks/useProgramTranslation';
import LanguageSwitcher from './LanguageSwitcher';

interface RegisterFormObject extends RegisterPayload {
	terms: boolean;
}

type RegisterInternalProps = {
	lastVisitedProgramName: string;
	programContext?: string;
};

const RegisterInternal: FC<RegisterInternalProps> = ({ lastVisitedProgramName, programContext }) => {
	const { i18n } = useTranslation();
	const { tProgram, t } = useProgramTranslationStatic({ programContext });
	const { toastRef } = useGlobal();

	const { control, handleSubmit, setError } = useForm<RegisterFormObject>({
		defaultValues: {
			email: '',
			terms: false,
		},
	});

	const {
		mutate,
		isSuccess,
		isLoading: isSubmitting,
	} = useMutation((data: RegisterPayload) => authApi.registerUser(data), {
		onError: (error) => {
			if (error instanceof AxiosError) {
				const data = error.response?.data;
				const message = data?.message;
				if (message) {
					if (message === 'userEmailExists') {
						const translated = t(`auth.messages.${message}`);
						setError('email', {
							type: 'custom',
							message: translated,
						});
					} else {
						toastRef?.current?.show({
							severity: 'error',
							life: 3000,
							summary: t('misc.error'),
							detail: data.message,
						});
					}
				} else {
					toastRef?.current?.show({
						severity: 'error',
						life: 3000,
						summary: t('misc.error'),
						detail: error.message,
					});
				}
			}
		},
	});

	const externalLogin = (type: string) => {
		const language = i18n.language.startsWith('pl') ? 'pl' : 'en';
		window.location.href = `${apiUrl}/auth/${type}/redirect?lng=${language}`;
	};

	const handleExternalLoginClick = (type: string) => {
		externalLogin(type);
	};

	const handleSubmitForm = handleSubmit(({ email }) =>
		mutate({ email, language: i18n.language, programName: lastVisitedProgramName })
	);

	if (isSuccess) {
		return (
			<>
				<div>{tProgram('auth.thankYouMessage')}</div>
				<p>{tProgram('auth.closeTabAndCheckInbox')}</p>
			</>
		);
	}

	return (
		<>
			<Helmet title={tProgram('auth.register')} />
			<form onSubmit={handleSubmitForm} className="flex flex-column row-gap-3">
				<div className="text-sm">
					<span>{tProgram('auth.registerTooltip1')}</span>
					<p>{tProgram('auth.registerTooltip2')}</p>
				</div>

				<TextField
					name="email"
					placeholder={tProgram('auth.inputs.email')}
					control={control}
					required
					autoComplete={generateAutocompleteId('login')}
				/>

				<Controller
					name="terms"
					control={control}
					rules={{ required: true }}
					render={({ field, fieldState }) => (
						<InputCheckbox
							inputId="termsOfService"
							label={tProgram('auth.accept')}
							invalid={Boolean(fieldState.error)}
							checked={Boolean(field.value)}
							onChange={(event) => field.onChange(Boolean(event.checked))}
							onBlur={field.onBlur}
							inputRef={field.ref}
							labelClassName="text-sm"
						>
							{tProgram('auth.accept')}{' '}
							<a
								href={translatedStaticResourceResolver('terms')}
								target="_blank"
								style={{ color: fieldState.error ? 'inherit' : colors.purplishBlue }}
								rel="noreferrer"
							>
								{tProgram('auth.termsOfService')}
							</a>
						</InputCheckbox>
					)}
				/>

				<Button submit label={tProgram('auth.buttons.sendLink')} loading={isSubmitting} block />

				<span className="text-center">{tProgram('misc.or')}</span>

				<div className="flex justify-content-between gap-2">
					<Button
						variant="primary-outlined"
						style={{ borderRadius: '12px' }}
						onClick={() => handleExternalLoginClick('google')}
						label={tProgram('auth.buttons.registerWithOffice')}
						block
						className="p-1 py-2"
					>
						<img src={GoogleIcon} alt="icon" />
						<span style={{ fontSize: '0.7rem' }}>{tProgram('auth.buttons.registerWithGoogle')}</span>
					</Button>
					<Button
						variant="primary-outlined"
						style={{ borderRadius: '12px' }}
						onClick={() => handleExternalLoginClick('microsoft')}
						label={tProgram('auth.buttons.registerWithOffice')}
						block
						className="p-1 py-2"
					>
						<img src={OfficeIcon} alt="icon" />
						<span style={{ fontSize: '0.7rem' }}>{tProgram('auth.buttons.registerWithOffice')}</span>
					</Button>
				</div>

				<div className="flex justify-content-center mt-4">
					<LanguageSwitcher />
				</div>
			</form>
		</>
	);
};

const Register: FC = () => {
	const navigate = useNavigate();
	const lastVisitedProgramName = window.localStorage.getItem(StoredKeys.ProgramName) as string | null;

	useEffect(() => {
		if (!lastVisitedProgramName) {
			navigate('/login');
		}
	}, [lastVisitedProgramName, navigate]);

	const [organizationName, programName] = String(lastVisitedProgramName || '').split('/');
	const {
		data: programData,
		isLoading: isProgramLoading,
		isSuccess: isProgramSuccess,
	} = useQuery(
		['REGISTER_PROGRAM', { organizationName, programName }],
		() => programsApi.getProgram(String(organizationName), String(programName)),
		{
			enabled: Boolean(lastVisitedProgramName && organizationName && programName),
			retry: false,
		}
	);

	useEffect(() => {
		if (
			lastVisitedProgramName &&
			(!organizationName || !programName || (!isProgramLoading && !isProgramSuccess && !programData))
		) {
			navigate('/login');
		}
	}, [
		isProgramLoading,
		isProgramSuccess,
		lastVisitedProgramName,
		navigate,
		organizationName,
		programData,
		programName,
	]);

	if (!lastVisitedProgramName || isProgramLoading || !programData) {
		return <Spinner />;
	}

	const programContext = programData.programType === 'coaching' ? 'coaching' : undefined;

	return <RegisterInternal lastVisitedProgramName={lastVisitedProgramName} programContext={programContext} />;
};

export default Register;
